/**
 * Drawing geometry of a counting line (same construction as the counting engine, services/vision geometry.py
 * curve_polyline): straight a→b, or the quadratic Bézier from a to b that passes through `curve` at its middle.
 */
export interface LinePoints {
  a: { x: number; y: number };
  b: { x: number; y: number };
  curve?: { x: number; y: number } | undefined;
}

/** Control point of the quadratic Bézier (null for a straight line). Same units as the input. */
export function lineControl(l: LinePoints): { x: number; y: number } | null {
  if (!l.curve) return null;
  return { x: 2 * l.curve.x - (l.a.x + l.b.x) / 2, y: 2 * l.curve.y - (l.a.y + l.b.y) / 2 };
}

/** Middle of the line (where the direction arrow sits): the bend point, or the midpoint of a straight line. */
export function lineMiddle(l: LinePoints): { x: number; y: number } {
  return l.curve ? { ...l.curve } : { x: (l.a.x + l.b.x) / 2, y: (l.a.y + l.b.y) / 2 };
}

/** Traces the line on a canvas in pixel space (w × h); the caller strokes. */
export function traceLine(g: { moveTo(x: number, y: number): void; lineTo(x: number, y: number): void; quadraticCurveTo(cx: number, cy: number, x: number, y: number): void }, l: LinePoints, w: number, h: number): void {
  const c = lineControl(l);
  g.moveTo(l.a.x * w, l.a.y * h);
  if (c) g.quadraticCurveTo(c.x * w, c.y * h, l.b.x * w, l.b.y * h);
  else g.lineTo(l.b.x * w, l.b.y * h);
}

/** SVG path data of the line in pixel space. */
export function linePathD(l: LinePoints, w: number, h: number): string {
  const c = lineControl(l);
  return c ? `M${l.a.x * w} ${l.a.y * h} Q${c.x * w} ${c.y * h} ${l.b.x * w} ${l.b.y * h}` : `M${l.a.x * w} ${l.a.y * h} L${l.b.x * w} ${l.b.y * h}`;
}

/**
 * Approach zone in front of the counting line (display only — counting stays on the line). The far edge is
 * the counting line a→b; the near edge is `zone` (a′ next to a, b′ next to b) or, when absent, the line moved
 * against the counted direction by 45 % of its length (clipped to the frame). Vehicles drive through the
 * zone towards the line in the arrow's direction.
 */
export interface ZoneLine extends LinePoints {
  countedDirection: 'A_TO_B' | 'B_TO_A';
  zone?: { a: { x: number; y: number }; b: { x: number; y: number } } | undefined;
}
export interface Zone {
  /** Near edge in normalised coordinates (a′ beside a, b′ beside b). */
  near: { a: { x: number; y: number }; b: { x: number; y: number } };
  /** Unit direction of travel in pixel space (the counted way across the line). */
  dir: { x: number; y: number };
  /**
   * Chevrons from the near edge towards the line (normalised anchor). Each points along the zone's own
   * axis (`dir`, pixel space: from the middle of the near edge to the middle of the line — the lanes'
   * direction in perspective) and is sized by the zone's width at that depth (`width`, pixels).
   */
  chevrons: { x: number; y: number; dir: { x: number; y: number }; width: number }[];
  /** True when the zone was drawn by hand (stored), false when derived. */
  custom: boolean;
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/** Counted direction as a unit vector in pixel space: A_TO_B is the right-hand normal of a→b (y down). */
export function countedNormal(l: ZoneLine, w: number, h: number): { x: number; y: number } {
  const dx = (l.b.x - l.a.x) * w;
  const dy = (l.b.y - l.a.y) * h;
  const len = Math.hypot(dx, dy) || 1;
  const s = l.countedDirection === 'A_TO_B' ? 1 : -1;
  return { x: (-dy / len) * s, y: (dx / len) * s };
}

export function lineZone(l: ZoneLine, w: number, h: number): Zone {
  const dir = countedNormal(l, w, h);
  let near = l.zone;
  const custom = !!near;
  if (!near) {
    const len = Math.hypot((l.b.x - l.a.x) * w, (l.b.y - l.a.y) * h);
    // Largest depth up to 45 % of the length that keeps both near corners inside the frame.
    let depth = len * 0.45;
    for (const p of [l.a, l.b]) {
      for (const [v, d, size] of [
        [p.x * w, -dir.x, w],
        [p.y * h, -dir.y, h],
      ] as const) {
        if (d > 1e-9) depth = Math.min(depth, (size - v) / d);
        else if (d < -1e-9) depth = Math.min(depth, v / -d);
      }
    }
    depth = Math.max(0, depth);
    near = {
      a: { x: clamp01(l.a.x - (dir.x * depth) / w), y: clamp01(l.a.y - (dir.y * depth) / h) },
      b: { x: clamp01(l.b.x - (dir.x * depth) / w), y: clamp01(l.b.y - (dir.y * depth) / h) },
    };
  }
  const farMid = lineMiddle(l);
  const nearMid = { x: (near.a.x + near.b.x) / 2, y: (near.a.y + near.b.y) / 2 };
  // Zone axis in pixels: the lanes' direction as the camera sees them (falls back to the line normal).
  const ax = (farMid.x - nearMid.x) * w;
  const ay = (farMid.y - nearMid.y) * h;
  const al = Math.hypot(ax, ay);
  // Pointing the counted way across the line (a hand-drawn zone could be dragged past the line).
  const axis = al > 1e-6 && ax * dir.x + ay * dir.y > 0 ? { x: ax / al, y: ay / al } : dir;
  const farW = Math.hypot((l.b.x - l.a.x) * w, (l.b.y - l.a.y) * h);
  const nearW = Math.hypot((near.b.x - near.a.x) * w, (near.b.y - near.a.y) * h);
  const chevrons = [0.28, 0.52, 0.76].map((t) => ({
    x: nearMid.x + (farMid.x - nearMid.x) * t,
    y: nearMid.y + (farMid.y - nearMid.y) * t,
    dir: axis,
    width: nearW + (farW - nearW) * t,
  }));
  return { near, dir, chevrons, custom };
}

/** Chevron "›" polyline points (pixels) for one zone chevron: wings across the lanes, tip along the axis. */
export function chevronPoints(c: { x: number; y: number; dir: { x: number; y: number }; width: number }, w: number, h: number, scale = 0.07): [[number, number], [number, number], [number, number]] {
  const cx = c.x * w;
  const cy = c.y * h;
  const s = Math.max(6, c.width * scale);
  const tx = -c.dir.y;
  const ty = c.dir.x;
  return [
    [cx - tx * s - c.dir.x * s * 0.55, cy - ty * s - c.dir.y * s * 0.55],
    [cx + c.dir.x * s * 0.45, cy + c.dir.y * s * 0.45],
    [cx + tx * s - c.dir.x * s * 0.55, cy + ty * s - c.dir.y * s * 0.55],
  ];
}

/** The canvas calls `drawGate` needs (a CanvasRenderingContext2D satisfies it; no DOM types here). */
export interface GateCanvas {
  globalAlpha: number;
  lineWidth: number;
  lineCap: string;
  lineJoin: string;
  strokeStyle: unknown;
  fillStyle: unknown;
  beginPath(): void;
  closePath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  quadraticCurveTo(cx: number, cy: number, x: number, y: number): void;
  arc(x: number, y: number, r: number, a0: number, a1: number): void;
  stroke(): void;
  fill(): void;
}

export const GATE_COLOUR = '#ffd21f';

/**
 * The counting gate as players see it, drawn on a canvas: a yellow approach zone in front of the line, chevrons
 * in the counted direction (perspective: smaller where the zone is narrower; a soft wave runs towards the line
 * unless `still`), and the counting line itself as the bold far edge with end posts. `flash` (0..1) turns the
 * line white for a moment after a counted vehicle. One drawing for the game and the operations portal.
 */
export function drawGate(g: GateCanvas, line: ZoneLine, w: number, h: number, o: { px: number; now?: number; dim?: boolean; flash?: number; still?: boolean }): void {
  const { px } = o;
  const flash = o.flash ?? 0;
  g.globalAlpha = o.dim ? 0.85 : 1;
  const ax = line.a.x * w;
  const ay = line.a.y * h;
  const bx = line.b.x * w;
  const by = line.b.y * h;
  const zone = lineZone(line, w, h);
  const na = { x: zone.near.a.x * w, y: zone.near.a.y * h };
  const nb = { x: zone.near.b.x * w, y: zone.near.b.y * h };
  g.lineCap = 'round';
  g.lineJoin = 'round';
  // Zone fill: the road area from the near edge to the counting line.
  g.beginPath();
  g.moveTo(na.x, na.y);
  g.lineTo(nb.x, nb.y);
  g.lineTo(bx, by);
  traceLine({ moveTo: (x, y) => g.lineTo(x, y), lineTo: (x, y) => g.lineTo(x, y), quadraticCurveTo: (cx, cy, x, y) => g.quadraticCurveTo(cx, cy, x, y) }, { a: line.b, b: line.a, curve: line.curve }, w, h);
  g.closePath();
  g.fillStyle = 'rgba(255,210,31,0.13)';
  g.fill();
  // Zone edges: thin near edge and side rails (dark casing + yellow).
  const edges: [number, number, number, number][] = [
    [na.x, na.y, nb.x, nb.y],
    [na.x, na.y, ax, ay],
    [nb.x, nb.y, bx, by],
  ];
  for (const [cw, cs] of [
    [px * 2.4, 'rgba(10,10,12,0.55)'],
    [px * 1.1, 'rgba(255,210,31,0.85)'],
  ] as const) {
    g.lineWidth = cw;
    g.strokeStyle = cs;
    for (const [x1, y1, x2, y2] of edges) {
      g.beginPath();
      g.moveTo(x1, y1);
      g.lineTo(x2, y2);
      g.stroke();
    }
  }
  const now = o.now ?? 0;
  zone.chevrons.forEach((c, i) => {
    const wave = o.still ? 0.85 : 0.55 + 0.45 * Math.max(0, Math.sin(now * 3.2 - i * 0.9));
    const pts = chevronPoints(c, w, h);
    const thick = Math.max(px * 1.6, c.width * 0.012);
    g.beginPath();
    g.moveTo(pts[0][0], pts[0][1]);
    g.lineTo(pts[1][0], pts[1][1]);
    g.lineTo(pts[2][0], pts[2][1]);
    g.lineWidth = thick * 1.8;
    g.strokeStyle = `rgba(10,10,12,${0.6 * wave})`;
    g.stroke();
    g.lineWidth = thick;
    g.strokeStyle = `rgba(255,210,31,${wave})`;
    g.stroke();
  });
  // The counting line: bold, with end posts; white flash on every counted vehicle.
  const core = flash > 0 ? `rgba(255,255,255,${0.7 + 0.3 * flash})` : GATE_COLOUR;
  const lw = px * (2.4 + 1.6 * flash);
  g.strokeStyle = 'rgba(10,10,12,0.75)';
  g.lineWidth = lw + px * 1.8;
  g.beginPath();
  traceLine(g, line, w, h);
  g.stroke();
  g.strokeStyle = core;
  g.lineWidth = lw;
  g.beginPath();
  traceLine(g, line, w, h);
  g.stroke();
  for (const [x, y] of [
    [ax, ay],
    [bx, by],
  ] as const) {
    g.fillStyle = 'rgba(10,10,12,0.8)';
    g.beginPath();
    g.arc(x, y, px * 2.8, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = core;
    g.beginPath();
    g.arc(x, y, px * 1.8, 0, Math.PI * 2);
    g.fill();
  }
  g.globalAlpha = 1;
}
